import { useEffect, useState } from "react";
import { Timer, Crown } from "lucide-react";
import { api } from "@/lib/api";
import useCountdown from "@/lib/useCountdown";

/**
 * Subtle, premium Founding-Coach countdown + live reservation counter.
 * Uses the real beta_ends_at from /api/founder-stats — never fabricates.
 *
 * variant: "banner" (default, horizontal) | "compact" (single line, tighter)
 */
export default function FounderCountdown({ variant = "banner", className = "" }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/founder-stats").then(r => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const countdown = useCountdown(stats?.beta_ends_at);

  if (!stats) return null; // don't render skeleton — stay subtle

  const { reserved, max } = stats;
  let progressCopy;
  if (max) {
    progressCopy = `${reserved} of ${max} Founding Coach spots reserved`;
  } else if (reserved >= 1) {
    progressCopy = `${reserved} ${reserved === 1 ? "coach has" : "coaches have"} reserved Founding Coach access`;
  } else {
    progressCopy = "Be among the first to reserve Founding Coach access";
  }

  const timeCopy = !countdown
    ? "Founding Coach access is available for a limited time."
    : countdown.ended
    ? "Beta window has closed"
    : (
      <>
        Founding Coach access closes in
        <span className="font-mono font-bold ml-1.5">
          {countdown.d}d {String(countdown.h).padStart(2, "0")}h {String(countdown.m).padStart(2, "0")}m {String(countdown.s).padStart(2, "0")}s
        </span>
      </>
    );

  if (variant === "compact") {
    return (
      <div
        data-testid="founder-countdown-compact"
        className={`inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-500 ${className}`}
      >
        <span className="inline-flex items-center gap-1.5"><Timer className="w-3 h-3 text-amber-500" /> {timeCopy}</span>
        <span className="hidden md:inline text-gray-300">·</span>
        <span className="inline-flex items-center gap-1.5"><Crown className="w-3 h-3 text-amber-500" /> {progressCopy}</span>
      </div>
    );
  }

  return (
    <div
      data-testid="founder-countdown"
      className={`inline-flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-white/80 backdrop-blur border border-amber-100 shadow-sm px-4 py-2.5 ${className}`}
    >
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-700"><Timer className="w-4 h-4 text-amber-500" /> <span>{timeCopy}</span></span>
      <span className="hidden md:inline w-px h-4 bg-gray-200" />
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-700"><Crown className="w-4 h-4 text-amber-500" /> <span data-testid="founder-progress">{progressCopy}</span></span>
    </div>
  );
}
